import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { ChevronLeft, ChevronRight, CalendarPlus, Trash2, Repeat, Clock, Copy, Repeat2, LayoutGrid, CalendarDays } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ClinicCalendarConnect from "@/components/ClinicCalendarConnect";
import TemperatureTherapyPanel from "@/components/TemperatureTherapyPanel";

const BODY_PARTS = [
  { id: "neck", label: "Neck" },
  { id: "shoulder", label: "Shoulders" },
  { id: "upper_back", label: "Upper Back" },
  { id: "lower_back", label: "Lower Back" },
  { id: "wrist", label: "Wrists" },
  { id: "elbow", label: "Elbows" },
  { id: "hand", label: "Hands" },
  { id: "hip", label: "Hips" },
  { id: "full_body", label: "Full Body" },
];

const RECURRENCE = [
  { id: "none", label: "One-time" },
  { id: "weekly", label: "Every week (6 months)" },
  { id: "biweekly", label: "Every 2 weeks (6 months)" },
  { id: "monthly", label: "Every month (6 months)" },
];

const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function ymdLocal(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function sameYmd(a, b) { return ymdLocal(a) === ymdLocal(b); }
function startOfMonthGrid(year, month) {
  const first = new Date(year, month, 1);
  const offset = first.getDay();
  const start = new Date(year, month, 1 - offset);
  return start;
}
function startOfWeek(d) {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  x.setDate(x.getDate() - x.getDay()); // roll back to Sunday
  return x;
}
function fmtTime(iso) {
  return new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

export default function StretchMonthCalendar({ canEdit = true, onChange, showTemperatureTherapy = false }) {
  const today = useMemo(() => new Date(), []);
  const [view, setView] = useState("month"); // "month" | "week"
  const [cursor, setCursor] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));
  const [weekAnchor, setWeekAnchor] = useState(() => startOfWeek(today));
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState("");
  const [openDay, setOpenDay] = useState(null); // Date instance or null
  const [refreshKey, setRefreshKey] = useState(0);

  const load = async () => {
    setErr("");
    try {
      const { data } = await api.get("/clinic/scheduled-stretches");
      setRows(data || []);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || "Unable to load schedule");
    }
  };
  useEffect(() => { load(); }, [refreshKey]);

  const monthLabel = cursor.toLocaleDateString(undefined, { month: "long", year: "numeric" });
  const gridStart = useMemo(() => startOfMonthGrid(cursor.getFullYear(), cursor.getMonth()), [cursor]);
  const days = useMemo(() => {
    return Array.from({ length: 42 }, (_, i) => {
      const d = new Date(gridStart); d.setDate(gridStart.getDate() + i); return d;
    });
  }, [gridStart]);
  const weekDays = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(weekAnchor); d.setDate(weekAnchor.getDate() + i); return d;
    });
  }, [weekAnchor]);
  const weekLabel = useMemo(() => {
    const first = weekDays[0]; const last = weekDays[6];
    const sameMonth = first.getMonth() === last.getMonth();
    const sameYear = first.getFullYear() === last.getFullYear();
    if (sameMonth) return `${first.toLocaleDateString(undefined, { month: "long" })} ${first.getDate()}\u2013${last.getDate()}, ${first.getFullYear()}`;
    if (sameYear)  return `${first.toLocaleDateString(undefined, { month: "short", day: "numeric" })} \u2013 ${last.toLocaleDateString(undefined, { month: "short", day: "numeric" })}, ${first.getFullYear()}`;
    return `${first.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })} \u2013 ${last.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}`;
  }, [weekDays]);

  const eventsByDay = useMemo(() => {
    const map = {};
    for (const r of rows) {
      const d = new Date(r.scheduled_at);
      const key = ymdLocal(d);
      (map[key] ||= []).push(r);
    }
    Object.values(map).forEach((list) => list.sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at)));
    return map;
  }, [rows]);

  const monthEventCount = days
    .filter((d) => d.getMonth() === cursor.getMonth())
    .reduce((acc, d) => acc + (eventsByDay[ymdLocal(d)]?.length || 0), 0);
  const weekEventCount = weekDays.reduce((acc, d) => acc + (eventsByDay[ymdLocal(d)]?.length || 0), 0);

  const prev = () => {
    if (view === "week") {
      const w = new Date(weekAnchor); w.setDate(w.getDate() - 7); setWeekAnchor(w);
    } else setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1));
  };
  const next = () => {
    if (view === "week") {
      const w = new Date(weekAnchor); w.setDate(w.getDate() + 7); setWeekAnchor(w);
    } else setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1));
  };
  const jumpToday = () => {
    if (view === "week") setWeekAnchor(startOfWeek(today));
    else setCursor(new Date(today.getFullYear(), today.getMonth(), 1));
  };

  const openForDay = (d) => {
    if (!canEdit) {
      // For read-only viewers, only open if there are events to inspect
      if (!eventsByDay[ymdLocal(d)]?.length) return;
    }
    setOpenDay(d);
  };

  const onDialogClose = (didChange) => {
    setOpenDay(null);
    if (didChange) {
      setRefreshKey((k) => k + 1);
      onChange?.();
    }
  };

  return (
    <div className="card-elev" data-testid="stretch-calendar">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h3 className="font-bold text-lg" data-testid="stretch-calendar-title">
            {view === "week" ? weekLabel : monthLabel}
          </h3>
          <p className="text-xs text-zinc-500 mt-0.5">
            {view === "week"
              ? `${weekEventCount} scheduled session${weekEventCount === 1 ? "" : "s"} this week`
              : `${monthEventCount} scheduled session${monthEventCount === 1 ? "" : "s"} this month`}
            {" · click a day to "}{canEdit ? "add one" : "view"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* View toggle */}
          <div className="inline-flex rounded-md border border-zinc-200 bg-white overflow-hidden" role="tablist" aria-label="Calendar view">
            <button
              type="button" role="tab" aria-selected={view === "month"}
              onClick={() => setView("month")}
              className={`h-9 px-3 text-xs font-bold inline-flex items-center gap-1 ${view === "month" ? "bg-zinc-900 text-white" : "text-zinc-600 hover:bg-zinc-50"}`}
              data-testid="stretch-cal-view-month"
            >
              <LayoutGrid className="w-3.5 h-3.5" /> Month
            </button>
            <button
              type="button" role="tab" aria-selected={view === "week"}
              onClick={() => setView("week")}
              className={`h-9 px-3 text-xs font-bold inline-flex items-center gap-1 border-l border-zinc-200 ${view === "week" ? "bg-zinc-900 text-white" : "text-zinc-600 hover:bg-zinc-50"}`}
              data-testid="stretch-cal-view-week"
            >
              <CalendarDays className="w-3.5 h-3.5" /> Week
            </button>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={prev} className="btn-ghost h-9 px-2" data-testid="stretch-cal-prev"><ChevronLeft className="w-4 h-4" /></button>
            <button onClick={jumpToday} className="btn-ghost h-9 px-3 text-xs" data-testid="stretch-cal-today">Today</button>
            <button onClick={next} className="btn-ghost h-9 px-2" data-testid="stretch-cal-next"><ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>
      </div>

      {err && <div className="text-sm text-red-600 mb-3" data-testid="stretch-cal-error">{err}</div>}

      {view === "month" ? (
        <div className="grid grid-cols-7 gap-px bg-zinc-100 rounded-lg overflow-hidden border border-zinc-200 text-sm">
          {DOW.map((d) => (
            <div key={d} className="bg-white px-2 py-2 text-[11px] font-bold uppercase tracking-[0.12em] text-zinc-500">{d}</div>
          ))}
          {days.map((d) => {
            const key = ymdLocal(d);
            const isToday = sameYmd(d, today);
            const inMonth = d.getMonth() === cursor.getMonth();
            const isPast = d < new Date(today.getFullYear(), today.getMonth(), today.getDate());
            const events = eventsByDay[key] || [];
            const dayClickable = canEdit || events.length > 0;
            return (
              <button
                key={key}
                type="button"
                onClick={() => openForDay(d)}
                disabled={!dayClickable}
                data-testid={`stretch-cal-day-${key}`}
                className={`bg-white text-left align-top p-2 min-h-[92px] hover:bg-zinc-50 transition ${
                  inMonth ? "" : "opacity-40"
                } ${dayClickable ? "cursor-pointer" : "cursor-default"}`}
              >
                <div className="flex items-center justify-between">
                  <span className={`text-xs font-bold ${isToday ? "text-white" : isPast ? "text-zinc-400" : "text-zinc-900"}`}>
                    <span
                      className={isToday ? "inline-flex items-center justify-center w-6 h-6 rounded-full" : ""}
                      style={isToday ? { background: "var(--gold)" } : {}}
                    >
                      {d.getDate()}
                    </span>
                  </span>
                  {events.length > 0 && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold" style={{ background: "var(--gold-light)", color: "var(--gold-hover)" }}>
                      {events.length}
                    </span>
                  )}
                </div>
                <div className="mt-1 space-y-1">
                  {events.slice(0, 2).map((e) => (
                    <div key={e.schedule_id} className="text-[11px] leading-tight truncate rounded px-1.5 py-0.5" style={{ background: "#fef9c3", color: "#78350f" }}>
                      {fmtTime(e.scheduled_at)} · {e.title}
                    </div>
                  ))}
                  {events.length > 2 && (
                    <div className="text-[10px] text-zinc-500">+{events.length - 2} more</div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      ) : (
        // ---------- Week view ----------
        <div className="grid grid-cols-1 md:grid-cols-7 gap-2" data-testid="stretch-cal-week">
          {weekDays.map((d) => {
            const key = ymdLocal(d);
            const isToday = sameYmd(d, today);
            const events = eventsByDay[key] || [];
            return (
              <div
                key={key}
                className={`rounded-lg border ${isToday ? "border-amber-300 ring-2 ring-amber-200" : "border-zinc-200"} bg-white p-2 flex flex-col gap-2`}
                data-testid={`stretch-cal-week-day-${key}`}
              >
                <button
                  type="button"
                  onClick={() => openForDay(d)}
                  disabled={!(canEdit || events.length > 0)}
                  className="flex items-start justify-between hover:opacity-80 transition text-left"
                >
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-zinc-400">
                      {d.toLocaleDateString(undefined, { weekday: "short" })}
                    </div>
                    <div className="text-lg font-black">
                      <span
                        className={isToday ? "inline-flex items-center justify-center w-8 h-8 rounded-full text-white" : ""}
                        style={isToday ? { background: "var(--gold)" } : {}}
                      >
                        {d.getDate()}
                      </span>
                    </div>
                  </div>
                  {events.length > 0 && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full font-bold" style={{ background: "var(--gold-light)", color: "var(--gold-hover)" }}>
                      {events.length}
                    </span>
                  )}
                </button>

                {/* Scheduled clinic sessions on this day */}
                <div className="space-y-1">
                  {events.slice(0, 3).map((e) => (
                    <div key={e.schedule_id} className="text-[11px] leading-tight rounded px-2 py-1" style={{ background: "#fef9c3", color: "#78350f" }}>
                      <div className="font-bold truncate">{e.title}</div>
                      <div className="text-[10px] opacity-80">{fmtTime(e.scheduled_at)} · {e.duration_minutes} min</div>
                    </div>
                  ))}
                  {events.length > 3 && (
                    <button type="button" onClick={() => openForDay(d)} className="text-[10px] text-zinc-500 hover:text-zinc-800">
                      +{events.length - 3} more
                    </button>
                  )}
                  {events.length === 0 && (
                    <div className="text-[11px] text-zinc-400 italic">No scheduled sessions</div>
                  )}
                </div>

                {/* Personal temperature therapy — only when opted in via the parent */}
                {showTemperatureTherapy && (
                  <TemperatureTherapyPanel dateIso={key} onChanged={() => onChange?.()} />
                )}
              </div>
            );
          })}
        </div>
      )}

      {openDay && (
        <DayDialog
          day={openDay}
          events={eventsByDay[ymdLocal(openDay)] || []}
          canEdit={canEdit}
          onClose={onDialogClose}
        />
      )}
    </div>
  );
}

function DayDialog({ day, events, canEdit, onClose }) {
  const [title, setTitle] = useState("Team stretch");
  const [time, setTime] = useState("14:00");
  const [duration, setDuration] = useState(15);
  const [parts, setParts] = useState([]);
  const [recurrence, setRecurrence] = useState("none");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [dirty, setDirty] = useState(false);

  const isPast = day < new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate());
  const humanDay = day.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" });

  const toggle = (id) => setParts((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!title.trim()) return setErr("Give the session a short title.");
    if (!time) return setErr("Pick a time.");
    if (!duration || duration <= 0) return setErr("Duration must be at least 1 minute.");
    setBusy(true);
    try {
      const localDt = new Date(`${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, "0")}-${String(day.getDate()).padStart(2, "0")}T${time}:00`);
      await api.post("/clinic/scheduled-stretches", {
        title: title.trim(),
        scheduled_at: localDt.toISOString(),
        duration_minutes: Number(duration),
        body_parts: parts,
        recurrence,
      });
      setDirty(true);
      setTitle("Team stretch"); setParts([]); setRecurrence("none");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const removeOne = async (id) => {
    await api.delete(`/clinic/scheduled-stretches/${id}`);
    setDirty(true);
  };
  const removeSeries = async (sid) => {
    await api.delete(`/clinic/scheduled-stretches/series/${sid}`);
    setDirty(true);
  };
  const clone = (e) => {
    setTitle(e.title || "Team stretch");
    setDuration(e.duration_minutes || 15);
    setParts(Array.isArray(e.body_parts) ? [...e.body_parts] : []);
    // Preserve time-of-day from the source event so the clone feels natural
    const src = new Date(e.scheduled_at);
    const hh = String(src.getHours()).padStart(2, "0");
    const mm = String(src.getMinutes()).padStart(2, "0");
    setTime(`${hh}:${mm}`);
    setRecurrence("none");
    setErr("");
    // Scroll form into view within the dialog for a quick confirm
    setTimeout(() => {
      document.querySelector('[data-testid="stretch-day-title"]')?.focus?.();
    }, 0);
  };

  const cloneNext4Weeks = async (e) => {
    setErr("");
    // Anchor the new weekly series on the FIRST occurrence AFTER the source
    // event, so the manager doesn't get a duplicate on this same day.
    const src = new Date(e.scheduled_at);
    const firstOccurrence = new Date(src.getTime() + 7 * 24 * 60 * 60 * 1000);
    try {
      await api.post("/clinic/scheduled-stretches", {
        title: e.title,
        scheduled_at: firstOccurrence.toISOString(),
        duration_minutes: e.duration_minutes,
        body_parts: Array.isArray(e.body_parts) ? e.body_parts : [],
        recurrence: "weekly",
        max_occurrences: 4,
      });
      setDirty(true);
    } catch (err2) {
      setErr(formatApiError(err2.response?.data?.detail) || err2.message);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(dirty); }}>
      <DialogContent className="max-w-2xl p-0 overflow-hidden" data-testid="stretch-day-dialog">
        <div className="px-6 pt-6 pb-4 border-b border-zinc-200">
          <DialogHeader>
            <DialogTitle className="text-lg">{humanDay}</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-zinc-500 mt-1">
            {events.length === 0 ? (canEdit ? "No sessions yet — add one below." : "No sessions on this day.") : `${events.length} session${events.length === 1 ? "" : "s"} on this day.`}
          </p>
        </div>

        <div className="max-h-[70vh] overflow-y-auto">
          {/* Existing events on this day */}
          {events.length > 0 && (
            <ul className="px-6 py-4 divide-y divide-zinc-100">
              {events.map((e) => (
                <li key={e.schedule_id} className="py-3 flex flex-wrap items-center justify-between gap-3" data-testid={`stretch-day-row-${e.schedule_id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold">
                      {e.title}
                      {e.recurrence && e.recurrence !== "none" && (
                        <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full uppercase tracking-[0.1em] font-bold" style={{ background: "var(--gold-light)", color: "var(--gold-hover)" }}>
                          <Repeat className="w-3 h-3 inline mr-1 -mt-0.5" />{e.recurrence}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-zinc-500 mt-0.5 flex items-center gap-2 flex-wrap">
                      <Clock className="w-3.5 h-3.5" />
                      {fmtTime(e.scheduled_at)} · {e.duration_minutes} min
                      {e.body_parts?.length > 0 && <span>· {e.body_parts.map((p) => p.replace(/_/g, " ")).join(", ")}</span>}
                    </div>
                  </div>
                  {canEdit && (
                    <div className="flex gap-2 flex-wrap justify-end">
                      <button onClick={() => clone(e)} className="btn-ghost text-xs h-9 px-3" data-testid={`stretch-day-clone-${e.schedule_id}`}>
                        <Copy className="w-4 h-4 mr-1" />Clone
                      </button>
                      <button onClick={() => cloneNext4Weeks(e)} className="btn-ghost text-xs h-9 px-3" data-testid={`stretch-day-clone4w-${e.schedule_id}`} title="Schedule this session weekly for the next 4 weeks">
                        <Repeat2 className="w-4 h-4 mr-1" />Copy × 4 weeks
                      </button>
                      <button onClick={() => removeOne(e.schedule_id)} className="btn-ghost text-red-600 text-xs h-9 px-3" data-testid={`stretch-day-del-${e.schedule_id}`}>
                        <Trash2 className="w-4 h-4 mr-1" />Remove
                      </button>
                      {e.series_id && (
                        <button onClick={() => removeSeries(e.series_id)} className="btn-ghost text-red-600 text-xs h-9 px-3" data-testid={`stretch-day-del-series-${e.series_id}`}>
                          Remove series
                        </button>
                      )}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}

          {/* Add form */}
          {canEdit && (
            <form onSubmit={submit} className="px-6 py-5 border-t border-zinc-200 space-y-4" data-testid="stretch-day-form">
              {isPast && (
                <div className="text-xs px-3 py-2 rounded bg-amber-50 text-amber-800 border border-amber-200">
                  This date is in the past — the session will be logged retroactively.
                </div>
              )}
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-zinc-600">Title</label>
                  <input type="text" maxLength={120} value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1 w-full h-10 rounded-md border border-zinc-200 px-3" data-testid="stretch-day-title" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-zinc-600 flex items-center gap-1"><Clock className="w-3.5 h-3.5" />Time</label>
                  <input type="time" value={time} onChange={(e) => setTime(e.target.value)} className="mt-1 w-full h-10 rounded-md border border-zinc-200 px-3" data-testid="stretch-day-time" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-zinc-600">Duration (min)</label>
                  <input type="number" min="1" max="240" step="1" value={duration} onChange={(e) => setDuration(e.target.value)} className="mt-1 w-full h-10 rounded-md border border-zinc-200 px-3" data-testid="stretch-day-duration" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-zinc-600 flex items-center gap-1"><Repeat className="w-3.5 h-3.5" />Repeat</label>
                  <select value={recurrence} onChange={(e) => setRecurrence(e.target.value)} className="mt-1 w-full h-10 rounded-md border border-zinc-200 px-3 bg-white" data-testid="stretch-day-recurrence">
                    {RECURRENCE.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <div className="text-xs font-semibold text-zinc-600 mb-1.5">Focus body parts (optional)</div>
                <div className="flex flex-wrap gap-1.5">
                  {BODY_PARTS.map((b) => (
                    <button
                      key={b.id}
                      type="button"
                      onClick={() => toggle(b.id)}
                      data-testid={`stretch-day-part-${b.id}`}
                      className={`px-3 py-1 rounded-full text-xs border transition ${parts.includes(b.id) ? "bg-zinc-900 text-white border-zinc-900" : "bg-white border-zinc-200 text-zinc-700"}`}
                    >{b.label}</button>
                  ))}
                </div>
              </div>
              {err && <div className="text-sm text-red-600" data-testid="stretch-day-error">{err}</div>}
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-[11px] text-zinc-500">Scheduled sessions auto-log and appear in every subscriber&apos;s calendar.</p>
                <button disabled={busy} className="btn-gold h-10 px-5 text-sm" data-testid="stretch-day-submit">
                  <CalendarPlus className="w-4 h-4 mr-2" />{busy ? "Adding\u2026" : "Add to day"}
                </button>
              </div>

              {/* Inline calendar-sync hint */}
              <div className="pt-4 border-t border-zinc-100">
                <ClinicCalendarConnect />
              </div>
            </form>
          )}

          {!canEdit && (
            <div className="px-6 py-5 border-t border-zinc-200">
              <ClinicCalendarConnect />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
